import React from "react";
import { ArrowRight, CheckCircle2, RefreshCw, RotateCcw, ShieldCheck, Sparkles, XCircle } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";

export default function ReturnPolicy() {
  const { settings } = useOutletContext();
  const appName = settings?.general?.appName || "Sugam Store";
  const returnDays = settings?.orders?.returnWindowDays || 7;
  const supportEmail = settings?.general?.supportEmail || "support@sugamstore.in";

  return (
    <main className="page-shell content-page policy-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Return & Refund Policy</span>
      </div>

      <div className="policy-header">
        <span className="eyebrow">
          <Sparkles size={14} /> 100% PEACE OF MIND
        </span>
        <h1>Return, Refund & Cancellation Policy</h1>
        <p className="policy-meta">
          Hassle-free {returnDays}-day return & replacement window on eligible products
        </p>
      </div>

      <div className="return-steps-grid">
        <div className="return-step-card">
          <span className="step-badge">1</span>
          <h3>Initiate Request</h3>
          <p>Request return or replacement within {returnDays} days of receiving the order.</p>
        </div>
        <div className="return-step-card">
          <span className="step-badge">2</span>
          <h3>Doorstep Pickup</h3>
          <p>Our courier partner picks up the item from your doorstep at zero extra cost.</p>
        </div>
        <div className="return-step-card">
          <span className="step-badge">3</span>
          <h3>Quick Inspection</h3>
          <p>Item is verified for original packaging and tags at our fulfillment hub.</p>
        </div>
        <div className="return-step-card">
          <span className="step-badge">4</span>
          <h3>Instant Refund</h3>
          <p>Refund processed to original source or bank UPI within 3 to 5 business days.</p>
        </div>
      </div>

      <div className="policy-container">
        <aside className="policy-nav-card">
          <h3>Table of Contents</h3>
          <nav>
            <a href="#return-window">1. Return Window & Eligibility</a>
            <a href="#non-returnable">2. Non-Returnable Items</a>
            <a href="#replacement-damaged">3. Damaged or Defective Items</a>
            <a href="#refund-process">4. Refund Methods & Timelines</a>
            <a href="#cancellation-policy">5. Order Cancellation Policy</a>
            <a href="#return-support">6. How to Contact Support</a>
          </nav>
        </aside>

        <article className="policy-body">
          <section id="return-window">
            <h2>1. Return Window & Eligibility Criteria</h2>
            <p>
              At <strong>{appName}</strong>, customer happiness is our topmost priority. If you are not completely satisfied 
              with your purchase, you can initiate a return or replacement within <strong>{returnDays} days</strong> from 
              the date of delivery.
            </p>
            <p>To be eligible for a return:</p>
            <ul>
              <li>The item must be unused, unwashed, and in the exact condition you received it.</li>
              <li>Original tags, user manuals, accessories, and manufacturer packaging must be intact.</li>
              <li>The item must be accompanied by the original invoice or order ID.</li>
            </ul>
          </section>

          <section id="non-returnable">
            <h2>2. Non-Returnable Items</h2>
            <p>For health, hygiene, and consumer safety reasons, certain categories cannot be returned once delivered:</p>
            <ul>
              <li>Personal care, grooming, and hygiene essentials once unsealed.</li>
              <li>Perishable consumables or food products.</li>
              <li>Items marked explicitly as “Final Sale” or “Non-Returnable” on the product detail page.</li>
              <li>Items damaged due to misuse, unauthorized handling, or accidental drops after delivery.</li>
            </ul>
          </section>

          <section id="replacement-damaged">
            <h2>3. Damaged, Defective, or Incorrect Deliveries</h2>
            <p>
              If you receive an item that is defective, damaged in transit, or different from what you ordered:
            </p>
            <div className="policy-callout">
              <RotateCcw size={20} />
              <div>
                <strong>Zero-Cost Replacement</strong>
                <p>
                  Notify us within 48 hours of delivery with photos or a short video. We will dispatch a brand-new 
                  replacement immediately at no extra charge, and arrange reverse pickup of the defective unit.
                </p>
              </div>
            </div>
          </section>

          <section id="refund-process">
            <h2>4. Refund Modes & Timelines</h2>
            <p>
              Once your returned item is received at our hub and completes quality inspection (usually within 24-48 hours), 
              your refund will be initiated:
            </p>
            <ul>
              <li>
                <strong>Prepaid Orders (Cards, Netbanking, Wallets):</strong> Refund is credited directly to the 
                original payment source within <strong>3 to 5 business days</strong> depending on your bank.
              </li>
              <li>
                <strong>UPI Orders (Google Pay, PhonePe, Paytm):</strong> Instant or same-day reversal 
                (usually within 2 to 24 hours).
              </li>
              <li>
                <strong>Cash on Delivery (COD) Orders:</strong> Our team will share a secure verification link 
                or request your UPI ID / bank NEFT details to transfer the refund amount within 
                <strong>2 to 4 business days</strong>.
              </li>
            </ul>
          </section>

          <section id="cancellation-policy">
            <h2>5. Order Cancellation</h2>
            <p>
              You can cancel an order free of charge at any time before it has been dispatched from our warehouse. 
              Simply visit <Link to="/orders">My Orders</Link> and click <em>Cancel Order</em>. 
              If the order was prepaid, a 100% refund will be initiated immediately.
            </p>
          </section>

          <section id="return-support">
            <h2>6. How to Initiate a Return</h2>
            <p>
              To initiate a return or exchange:
            </p>
            <ol>
              <li>Go to your <Link to="/orders">Orders</Link> section and select the item you wish to return.</li>
              <li>Alternatively, write to us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a> or reach out on <Link to="/contact">Contact Us</Link> with your order number.</li>
              <li>Our team will approve the request and schedule a courier pickup at your convenience.</li>
            </ol>
          </section>
        </article>
      </div>
    </main>
  );
}
